import { z } from 'zod';

// ---------- Units and money ----------
// Balances are integers in the unit's smallest step ("minor units") and never floats, so sums are exact.

export const KASA_UNITS = ['TRY', 'USD', 'EUR', 'XAU', 'BTC', 'CUSTOM'] as const;
export type KasaUnit = (typeof KASA_UNITS)[number];

/** Decimal places kept per unit: kuruş and cent, milligram of gold, satoshi. */
export const UNIT_SCALE: Record<KasaUnit, number> = { TRY: 2, USD: 2, EUR: 2, XAU: 3, BTC: 8, CUSTOM: 2 };

/** Comfortably inside Number.MAX_SAFE_INTEGER so sums of thousands of entries stay exact. */
export const MAX_MINOR = 1_000_000_000_000_000;

/** 1 minor unit expressed as a plain number of the unit. */
export const minorToUnits = (minor: number, unit: KasaUnit): number => minor / 10 ** UNIT_SCALE[unit];

/**
 * Reads a user-typed amount ("1.234,56", "1234,5", "12.5", "1 250") into minor units.
 * A comma is always the decimal mark; a lone dot is decimal only when it cannot be a thousands
 * separator (not followed by exactly three digits). Returns null for anything else, and for more
 * decimals than the unit keeps, instead of silently rounding money.
 */
export function parseAmount(text: string, unit: KasaUnit): number | null {
  return parseDecimal(text, UNIT_SCALE[unit]);
}

/** `parseAmount` for any number of decimal places, e.g. the millionths of a fixed unit rate. */
export function parseDecimal(text: string, scale: number): number | null {
  const cleaned = text.trim().replace(/[\s ]/g, '');
  if (!/^\d[\d.,]*$/.test(cleaned)) return null;
  let integer: string;
  let fraction = '';
  if (cleaned.includes(',')) {
    const parts = cleaned.split(',');
    if (parts.length !== 2) return null;
    integer = parts[0]!.replace(/\./g, '');
    fraction = parts[1]!;
    if (!/^\d+$/.test(integer) || !/^\d*$/.test(fraction)) return null;
  } else {
    const dots = cleaned.split('.');
    if (dots.length === 2 && !/^\d{3}$/.test(dots[1]!)) {
      integer = dots[0]!;
      fraction = dots[1]!;
    } else {
      integer = dots.join('');
    }
    if (!/^\d+$/.test(integer) || !/^\d*$/.test(fraction)) return null;
  }
  if (fraction.length > scale) return null;
  const minor = Number(integer + fraction.padEnd(scale, '0'));
  return Number.isSafeInteger(minor) && minor <= MAX_MINOR ? minor : null;
}

/** Converts an amount between units at `rate` (target units per 1 source unit), rounding to the nearest minor unit. */
export function convertMinor(minor: number, from: KasaUnit, to: KasaUnit, rate: number): number {
  return Math.round(minorToUnits(minor, from) * rate * 10 ** UNIT_SCALE[to]);
}

/** Value in TRY kuruş of a balance, given the TRY price of one whole unit. */
export function valueTryMinor(minor: number, unit: KasaUnit, tryPerUnit: number): number {
  return Math.round(minorToUnits(minor, unit) * tryPerUnit * 100);
}

// ---------- Accounts ----------

export const ACCOUNT_KINDS = ['cash', 'bank', 'investment', 'receivable', 'debt', 'other'] as const;
export type AccountKind = (typeof ACCOUNT_KINDS)[number];

const minorAmount = z.number().int().min(-MAX_MINOR).max(MAX_MINOR);
const positiveMinor = z.number().int().min(1).max(MAX_MINOR);
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

const accountFields = {
  name: z.string().trim().min(1).max(60),
  kind: z.enum(ACCOUNT_KINDS),
  unit: z.enum(KASA_UNITS),
  /** Name and fixed TRY price (millionths of a lira per unit) of a user-defined unit. */
  customName: z.string().trim().min(1).max(12).nullable(),
  customRateMicro: z.number().int().min(1).max(MAX_MINOR).nullable(),
  openingMinor: minorAmount,
  archived: z.boolean(),
  sort: z.number().int().min(0).max(100_000),
  color: z.string().regex(/^#[0-9a-fA-F]{6}$/).nullable(),
  icon: z.string().trim().max(24).nullable(),
};

const customUnitComplete = (value: { unit?: KasaUnit; customName?: string | null; customRateMicro?: number | null }) =>
  value.unit !== 'CUSTOM' || (Boolean(value.customName) && Boolean(value.customRateMicro));

export const accountInput = z
  .object({
    ...accountFields,
    customName: accountFields.customName.default(null),
    customRateMicro: accountFields.customRateMicro.default(null),
    openingMinor: accountFields.openingMinor.default(0),
    archived: accountFields.archived.default(false),
    sort: accountFields.sort.default(0),
    color: accountFields.color.default(null),
    icon: accountFields.icon.default(null),
  })
  .refine(customUnitComplete, { message: 'A custom unit needs a name and a rate.', path: ['customName'] });
export type AccountInput = z.infer<typeof accountInput>;

export const accountPatch = z.object(accountFields).partial();

export interface KasaAccount extends AccountInput {
  id: string;
  /** Opening balance plus every entry, in the account's minor units. */
  balanceMinor: number;
}

// ---------- Entries ----------

export const DEFAULT_CATEGORIES = ['Maaş', 'Kira', 'Market', 'Fatura', 'Yakıt', 'Borç ödeme', 'Yatırım', 'Diğer'];
export const categoriesInput = z.object({
  categories: z.array(z.string().trim().min(1).max(40)).max(60),
});

export const entryInput = z.object({
  accountId: z.uuid(),
  date: isoDate,
  direction: z.enum(['in', 'out']),
  amountMinor: positiveMinor,
  category: z.string().trim().max(40).default(''),
  description: z.string().trim().max(200).default(''),
});
export type EntryInput = z.infer<typeof entryInput>;

export const entryPatch = z
  .object({
    accountId: z.uuid(),
    date: isoDate,
    direction: z.enum(['in', 'out']),
    amountMinor: positiveMinor,
    category: z.string().trim().max(40),
    description: z.string().trim().max(200),
  })
  .partial();

export const transferInput = z
  .object({
    fromAccountId: z.uuid(),
    toAccountId: z.uuid(),
    date: isoDate,
    /** Leaves the source account, in its units. */
    fromAmountMinor: positiveMinor,
    /** Arrives in the target account, in its units; the exchange rate follows from the two amounts. */
    toAmountMinor: positiveMinor,
    category: z.string().trim().max(40).default(''),
    description: z.string().trim().max(200).default(''),
  })
  .refine((value) => value.fromAccountId !== value.toAccountId, { message: 'Pick two different accounts.', path: ['toAccountId'] });
export type TransferInput = z.infer<typeof transferInput>;

export interface KasaEntry extends EntryInput {
  id: string;
  /** Set on both legs of a transfer between accounts. */
  transferId: string | null;
  /** Units of the other account per unit of this one, times 1e6, on transfer legs. */
  rateMicro: number | null;
}

export const entryQuery = z.object({
  from: isoDate.optional(),
  to: isoDate.optional(),
  account: z.uuid().optional(),
  category: z.string().max(40).optional(),
  q: z.string().trim().max(80).optional(),
  limit: z.coerce.number().int().min(1).max(500).default(100),
  offset: z.coerce.number().int().min(0).default(0),
});
export interface EntryListing {
  entries: KasaEntry[];
  total: number;
}

export const snapshotsQuery = z.object({ days: z.coerce.number().int().min(1).max(730).default(90) });
export const monthlyQuery = z.object({ year: z.coerce.number().int().min(2000).max(2100) });

// ---------- Net worth ----------

export type PricedUnit = 'USD' | 'EUR' | 'XAU' | 'BTC';
/** TRY price of one whole unit (one dollar, one gram of gold, one bitcoin). */
export type UnitRates = Partial<Record<PricedUnit, number>>;

/** TRY prices of the priced units from the shared market quotes (gold is quoted per gram, Bitcoin in dollars). */
export function ratesFromMarkets(items: { id: string; value: number }[]): UnitRates {
  const value = (id: string) => items.find((item) => item.id === id)?.value;
  const usd = value('usd');
  const btc = value('btc');
  return {
    USD: usd,
    EUR: value('eur'),
    XAU: value('gold'),
    BTC: usd !== undefined && btc !== undefined ? btc * usd : undefined,
  };
}

export interface NetWorthAccount {
  id: string;
  kind: AccountKind;
  unit: KasaUnit;
  customName: string | null;
  customRateMicro: number | null;
  balanceMinor: number;
}

export interface NetWorthBreakdownRow {
  key: string;
  label: string;
  valueTryMinor: number;
}
export interface NetWorth {
  totalTryMinor: number;
  assetsTryMinor: number;
  liabilitiesTryMinor: number;
  byUnit: NetWorthBreakdownRow[];
  byKind: (Omit<NetWorthBreakdownRow, 'key'> & { key: AccountKind })[];
  /** Each priced account's signed value (debts negative). */
  accountValues: { id: string; valueTryMinor: number }[];
  /** Accounts left out of the total because the price of their unit is unknown right now. */
  unpricedAccountIds: string[];
}

export function tryPerUnit(account: Pick<NetWorthAccount, 'unit' | 'customRateMicro'>, rates: UnitRates): number | undefined {
  if (account.unit === 'TRY') return 1;
  if (account.unit === 'CUSTOM') return account.customRateMicro ? account.customRateMicro / 1e6 : undefined;
  return rates[account.unit];
}

/**
 * Assets minus debts in TRY. A debt account's balance is the amount owed, so it counts negatively;
 * every other kind counts as it stands.
 */
export function computeNetWorth(accounts: NetWorthAccount[], rates: UnitRates): NetWorth {
  const byUnit = new Map<string, NetWorthBreakdownRow>();
  const byKind = new Map<AccountKind, number>();
  const unpricedAccountIds: string[] = [];
  const accountValues: NetWorth['accountValues'] = [];
  let assets = 0;
  let liabilities = 0;
  for (const account of accounts) {
    const price = tryPerUnit(account, rates);
    if (price === undefined) {
      unpricedAccountIds.push(account.id);
      continue;
    }
    const value = valueTryMinor(account.balanceMinor, account.unit, price);
    const signed = account.kind === 'debt' ? -value : value;
    accountValues.push({ id: account.id, valueTryMinor: signed });
    if (signed >= 0) assets += signed;
    else liabilities += -signed;
    byKind.set(account.kind, (byKind.get(account.kind) ?? 0) + signed);
    const label = account.unit === 'CUSTOM' ? (account.customName ?? 'Özel') : account.unit;
    const row = byUnit.get(label) ?? { key: label, label, valueTryMinor: 0 };
    row.valueTryMinor += signed;
    byUnit.set(label, row);
  }
  return {
    totalTryMinor: assets - liabilities,
    assetsTryMinor: assets,
    liabilitiesTryMinor: liabilities,
    byUnit: [...byUnit.values()].sort((a, b) => Math.abs(b.valueTryMinor) - Math.abs(a.valueTryMinor)),
    byKind: [...byKind].map(([key, valueTryMinor]) => ({ key, label: key, valueTryMinor })),
    accountValues,
    unpricedAccountIds,
  };
}

export interface NetWorthSummary {
  netWorth: NetWorth;
  accountCount: number;
  /** Net worth in the latest snapshot before today, for the day-over-day change. */
  previousTryMinor: number | null;
  stale: boolean;
}

export interface NetWorthSnapshot {
  date: string;
  netWorthTryMinor: number;
}

export interface MonthlySummaryRow {
  month: string;
  incomeTryMinor: number;
  expenseTryMinor: number;
}
